import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Check, ChefHat, ClipboardList, ClipboardCheck, CloudOff, Loader2, Minus, Pencil, Plus, Printer, Send, Wine, XCircle, RefreshCw, EyeOff } from "lucide-react";
import { performStockAction } from "@/lib/stockSync";
import { EventType, uuidV4 } from "@/lib/events";
import { cachedGet } from "@/lib/refCache";
import { realtime } from "@/lib/realtime";
import { fireAndForgetMesh } from "@/lib/lanMesh";
import { EVENT_SYNC_FLUSHED } from "@/lib/eventSync";

/**
 * Session 41 — Kitchen / Bar PREP EXECUTION (station-scoped, full screen).
 *
 *   The manager's target is READ-ONLY here. Staff record what they actually
 *   prepared (a separate number), mark lines prepared / unable, and submit
 *   the sheet. Nothing here moves stock — the manager confirms production
 *   from the Owner dashboard afterwards.
 *
 *   Every write goes through `performStockAction` (Session 40 pipeline):
 *   online → HTTP with a client change_id; offline / network error → the
 *   SAME payload is queued via localWrite (LAN mesh → coordinator → cloud)
 *   and replayed idempotently. Local state is optimistic so the screen
 *   keeps working on a router-only network.
 */
const fmt = (n) => (n == null || !Number.isFinite(Number(n)) ? "—" : (Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2).replace(/\.?0+$/, "")));
const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return (typeof d === "string" ? d : d?.message) || fb; };
const stepFor = (unit) => (["g", "ml"].includes(String(unit || "").toLowerCase()) ? 50 : ["kg", "l"].includes(String(unit || "").toLowerCase()) ? 0.5 : 1);
const scopeLabel = (s) => ({ floor: "Floor", boh: "Back of house", both: "Floor + Back of house" }[s] || s);

export const PrepExecutionPanel = ({ http, headers, station = "kitchen", session = null, onBack, embedded = false }) => {
  const [data, setData] = useState(null); // {active:[], recent:[]}
  const [fromCache, setFromCache] = useState(false);
  const [sheetId, setSheetId] = useState(null);
  const [drafts, setDrafts] = useState({}); // line id → { qty, note, editing }
  const [busy, setBusy] = useState("");
  const [unableDlg, setUnableDlg] = useState(null); // { line }
  const [submitDlg, setSubmitDlg] = useState(false);
  const [submitNote, setSubmitNote] = useState("");
  const [queuedIds, setQueuedIds] = useState(() => new Set());
  const [pendingLocal, setPendingLocal] = useState({}); // line id → optimistic patch (until server confirms)
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const Icon = station === "bar" ? Wine : ChefHat;
  const stationLabel = station === "bar" ? "Bar" : "Kitchen";
  const userName = session?.user?.full_name || session?.user?.name || session?.staff?.name || null;

  const load = useCallback(async (silent = false) => {
    if (!headers) return;
    try {
      const r = await cachedGet(http, "/station/prep-sheets", { headers, params: { station } });
      if (!mounted.current) return;
      setData(r.data || { active: [], recent: [] });
      setFromCache(!!r.fromCache);
      // Server state wins over optimistic patches once it reflects them.
      setPendingLocal({});
    } catch (e) {
      if (!mounted.current) return;
      if (!silent) toast.error(errMsg(e, "Couldn't load the prep sheet."));
      setData((d) => d || { active: [], recent: [] });
    }
  }, [http, headers, station]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Live: manager sent / re-sent / cancelled / confirmed a sheet — arrives
    // over cloud WS or the LAN mesh (both feed the same bus).
    let timer = null;
    const unsub = realtime.subscribe((msg) => {
      const t = String(msg?.type || "");
      if (!/^prep_|^production_/.test(t)) return;
      const st = msg?.payload?.station;
      if (st && st !== station) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; load(true); }, 500);
    });
    const onFlushed = () => load(true);
    window.addEventListener(EVENT_SYNC_FLUSHED, onFlushed);
    const iv = setInterval(() => load(true), 45000);
    return () => { unsub(); if (timer) clearTimeout(timer); clearInterval(iv); window.removeEventListener(EVENT_SYNC_FLUSHED, onFlushed); };
  }, [load, station]);

  const active = useMemo(() => (data?.active || []), [data]);
  const sheet = useMemo(() => {
    const s = active.find((x) => x.id === sheetId) || active[0] || null;
    if (!s) return null;
    // Apply optimistic patches so an offline tap is visible instantly.
    if (!Object.keys(pendingLocal).length) return s;
    return { ...s, lines: (s.lines || []).map((l) => (pendingLocal[l.id] ? { ...l, ...pendingLocal[l.id] } : l)) };
  }, [active, sheetId, pendingLocal]);
  const lines = sheet?.lines || [];
  // Session 64 — STOCK READ: blind count. No target, no expected, no variance on this screen.
  const isRead = sheet?.sheet_type === "stock_read";
  const scope = sheet?.location_scope || "both";
  const wantFloor = isRead && ["floor", "both"].includes(scope);
  const wantBoh = isRead && ["boh", "both"].includes(scope);
  const done = lines.filter((l) => ["prepared", "unable", "counted"].includes(l.status)).length;
  const pct = lines.length ? Math.round((done / lines.length) * 100) : 0;

  const draftFor = (l) => drafts[l.id] || { qty: l.actual_qty != null ? String(l.actual_qty) : String(l.target_qty ?? ""), floor: l.counted_floor != null ? String(l.counted_floor) : "", boh: l.counted_boh != null ? String(l.counted_boh) : "", note: l.staff_note || "", editing: false };
  const setDraft = (l, patch) => setDrafts((d) => ({ ...d, [l.id]: { ...draftFor(l), ...patch } }));

  const meshPing = (type, payload) => {
    try {
      fireAndForgetMesh({ change_id: uuidV4(), event_type: type, ts: new Date().toISOString(), payload: { ...payload, station, sheet_id: sheet?.id, title: sheet?.title } }, { targetRole: "*" });
    } catch (_) { /* best-effort */ }
  };

  // Session 64 — save a blind count for one ingredient (floor and/or BOH).
  const recordCount = async (l) => {
    if (!sheet) return;
    const d = draftFor(l);
    const floor = wantFloor ? d.floor : null;
    const boh = wantBoh ? d.boh : null;
    if ((wantFloor && String(floor).trim() === "") || (wantBoh && String(boh).trim() === "")) { toast.error(scope === "both" ? "Enter both the floor and the back of house count." : "Enter the count first."); return; }
    const body = { status: "counted", floor_qty: wantFloor ? Math.max(0, Number(floor) || 0) : null, boh_qty: wantBoh ? Math.max(0, Number(boh) || 0) : null, note: d.note || null, user: userName };
    setBusy(l.id);
    setPendingLocal((p) => ({ ...p, [l.id]: { status: "counted", counted_floor: body.floor_qty, counted_boh: body.boh_qty, staff_note: body.note || l.staff_note, counted_by: userName || l.counted_by || "Station" } }));
    try {
      const res = await performStockAction({
        http, authHeaders: headers, path: `/station/prep-sheets/${sheet.id}/lines/${l.id}`, body,
        eventType: EventType.PREP_PROGRESS_RECORDED,
        eventPayload: { ...body, sheet_id: sheet.id, line_id: l.id, line_name: l.name, station, title: sheet.title },
        session,
      });
      if (res.status === "queued") {
        setQueuedIds((s) => new Set([...s, l.id]));
        toast.info("Count saved on this device — will sync when the connection returns.", { id: "prep-queued" });
      } else {
        setQueuedIds((s) => { const n = new Set(s); n.delete(l.id); return n; });
        if (res.data?.sheet) setData((cur) => ({ ...(cur || {}), active: (cur?.active || []).map((s) => (s.id === res.data.sheet.id ? res.data.sheet : s)) }));
        setPendingLocal((p) => { const n = { ...p }; delete n[l.id]; return n; });
        meshPing("prep_progress", { line_id: l.id, line_name: l.name, status: "counted", by: userName, summary: res.data?.sheet?.summary });
      }
      setDrafts((x) => { const n = { ...x }; delete n[l.id]; return n; });
    } catch (e) {
      setPendingLocal((p) => { const n = { ...p }; delete n[l.id]; return n; });
      toast.error(errMsg(e, "Couldn't save this count."));
    } finally { setBusy(""); }
  };

  const recordLine = async (l, status, extra = {}) => {
    if (!sheet) return;
    const d = draftFor(l);
    const actual = l.kind === "produce" ? (status === "prepared" ? Math.max(0, Number(d.qty) || 0) : Number(extra.actual_qty ?? 0) || 0) : null;
    const body = { status, actual_qty: l.kind === "produce" ? actual : null, note: extra.note !== undefined ? extra.note : (d.note || null), user: userName };
    setBusy(l.id);
    // Optimistic: the card flips immediately, online or not.
    setPendingLocal((p) => ({ ...p, [l.id]: { status, actual_qty: body.actual_qty, staff_note: body.note || l.staff_note, prepared_by: userName || l.prepared_by || "Station" } }));
    try {
      const res = await performStockAction({
        http, authHeaders: headers, path: `/station/prep-sheets/${sheet.id}/lines/${l.id}`, body,
        eventType: EventType.PREP_PROGRESS_RECORDED,
        eventPayload: { ...body, sheet_id: sheet.id, line_id: l.id, line_name: l.name, target_qty: l.target_qty, station, title: sheet.title },
        session,
      });
      if (res.status === "queued") {
        setQueuedIds((s) => new Set([...s, l.id]));
        toast.info("Saved on this device — will sync when the connection returns.", { id: "prep-queued" });
      } else {
        setQueuedIds((s) => { const n = new Set(s); n.delete(l.id); return n; });
        if (res.data?.sheet) setData((cur) => ({ ...(cur || {}), active: (cur?.active || []).map((s) => (s.id === res.data.sheet.id ? res.data.sheet : s)) }));
        setPendingLocal((p) => { const n = { ...p }; delete n[l.id]; return n; });
        meshPing("prep_progress", { line_id: l.id, line_name: l.name, status, actual_qty: body.actual_qty, target_qty: l.target_qty, by: userName, summary: res.data?.sheet?.summary });
      }
      setDrafts((x) => { const n = { ...x }; delete n[l.id]; return n; });
    } catch (e) {
      setPendingLocal((p) => { const n = { ...p }; delete n[l.id]; return n; });
      toast.error(errMsg(e, "Couldn't save this line."));
    } finally { setBusy(""); }
  };

  const submit = async () => {
    if (!sheet) return;
    setBusy("submit");
    try {
      const res = await performStockAction({
        http, authHeaders: headers, path: `/station/prep-sheets/${sheet.id}/submit`, body: { note: submitNote || null, user: userName },
        eventType: EventType.PREP_SHEET_SUBMITTED,
        eventPayload: { sheet_id: sheet.id, note: submitNote || null, user: userName, station, title: sheet.title, summary: sheet.summary },
        session,
      });
      setSubmitDlg(false); setSubmitNote("");
      if (res.status === "queued") {
        toast.info("Submitted on this device — the manager will receive it when the connection returns.");
        setPendingLocal({});
        setData((cur) => ({ ...(cur || {}), active: (cur?.active || []).filter((s) => s.id !== sheet.id), recent: [{ ...sheet, status: "submitted", _queued: true }, ...(cur?.recent || [])] }));
      } else {
        toast.success(isRead ? "Counts sent to the manager." : `Prep sheet sent to the manager for confirmation.`);
        meshPing("prep_sheet_submitted", { by: userName, summary: res.data?.sheet?.summary, variance: res.data?.sheet?.variance });
        await load(true);
      }
    } catch (e) { toast.error(errMsg(e, "Couldn't submit the prep sheet.")); } finally { setBusy(""); }
  };

  // ── Render ────────────────────────────────────────────────────────────
  const header = (
    <div className="flex flex-wrap items-center gap-3" data-testid="prep-exec-header">
      {onBack ? <Button variant="outline" className="h-12 rounded-xl" onClick={onBack} data-testid="prep-exec-back"><ArrowLeft className="h-4 w-4" /> Orders</Button> : null}
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[hsl(var(--primary))] text-primary-foreground">{isRead ? <ClipboardCheck className="h-6 w-6" /> : <Icon className="h-6 w-6" />}</span>
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wide text-muted-foreground" data-testid="prep-exec-kicker">{isRead ? `Stock read · ${sheet?.department_name || stationLabel} · count ${scopeLabel(scope)}` : `${stationLabel} prep · today`}</p>
        <h2 className="truncate font-['Space_Grotesk'] text-2xl font-semibold sm:text-3xl" data-testid="prep-exec-title">{sheet ? sheet.title : "No prep sheet yet"}</h2>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {fromCache ? <Badge variant="outline" className="h-9 gap-1 rounded-xl border-amber-500/50 text-amber-700 dark:text-amber-300" data-testid="prep-exec-cache-badge"><CloudOff className="h-3.5 w-3.5" /> Last known</Badge> : null}
        <Button variant="outline" className="h-12 rounded-xl" onClick={() => load()} data-testid="prep-exec-refresh" aria-label="Refresh"><RefreshCw className="h-4 w-4" /></Button>
        {sheet ? <Button variant="outline" className="h-12 rounded-xl" onClick={() => window.print()} data-testid="prep-exec-print"><Printer className="h-4 w-4" /> <span className="hidden sm:inline">Print</span></Button> : null}
        {sheet ? (
          <Button className="h-12 rounded-xl bg-[hsl(var(--accent))] px-5 text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" disabled={!!busy || !lines.length} onClick={() => setSubmitDlg(true)} data-testid="prep-exec-submit">
            <Send className="h-4 w-4" /> {isRead ? "Submit counts" : "Submit to manager"}
          </Button>
        ) : null}
      </div>
    </div>
  );

  if (data === null) {
    return <div className="space-y-4" data-testid="prep-exec-loading">{header}<Skeleton className="h-24 rounded-2xl" /><Skeleton className="h-24 rounded-2xl" /></div>;
  }

  return (
    <div className={embedded ? "space-y-4" : "space-y-4"} data-testid="prep-exec-panel">
      {header}

      {active.length > 1 ? (
        <div className="flex flex-wrap gap-2" role="tablist" data-testid="prep-exec-sheet-tabs">
          {active.map((s) => <button key={s.id} type="button" role="tab" aria-selected={sheet?.id === s.id} onClick={() => setSheetId(s.id)} className={`h-11 rounded-xl border px-4 text-sm font-semibold ${sheet?.id === s.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`} data-testid={`prep-exec-tab-${s.id}`}>{s.title}</button>)}
        </div>
      ) : null}

      {!sheet ? (
        <Card className="rounded-[28px] border-2 border-dashed border-border bg-card">
          <CardContent className="flex flex-col items-center justify-center gap-3 py-14 text-center" data-testid="prep-exec-empty">
            <ClipboardList className="h-10 w-10 text-muted-foreground" />
            <p className="font-['Space_Grotesk'] text-2xl font-semibold">Nothing to prep or count right now</p>
            <p className="max-w-md text-sm text-muted-foreground">When a manager sends today's {stationLabel.toLowerCase()} prep sheet or a stock read it appears here automatically. You can keep working from a printed sheet in the meantime.</p>
            {(data.recent || []).length ? (
              <div className="mt-3 w-full max-w-lg space-y-2 text-left">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Recently submitted</p>
                {(data.recent || []).slice(0, 4).map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-2xl border border-border bg-background px-4 py-3" data-testid={`prep-exec-recent-${s.id}`}>
                    <div><p className="font-semibold">{s.title}</p><p className="text-xs text-muted-foreground">{s.summary?.prepared || 0} prepared · {s.summary?.unable || 0} unable{s._queued ? " · waiting to sync" : ""}</p></div>
                    <Badge variant="outline" className="rounded-full">{s.status === "confirmed" ? "Production confirmed" : "With manager"}</Badge>
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Progress */}
          <Card className="rounded-[24px] border-border bg-card" data-testid="prep-exec-progress">
            <CardContent className="flex flex-wrap items-center gap-4 p-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm font-semibold">{done} of {lines.length} {isRead ? "counted" : "done"}</p>
                  <p className="text-xs text-muted-foreground">{isRead ? `${lines.length - done} to go` : `${sheet.summary?.prepared || 0} prepared · ${sheet.summary?.unable || 0} unable`}{sheet.sent_by ? ` · sent by ${sheet.sent_by}` : ""}</p>
                </div>
                <div className="mt-2 h-3 overflow-hidden rounded-full bg-secondary" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                  <div className="h-full rounded-full bg-[hsl(var(--primary))] transition-[width] duration-200" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <Badge className="rounded-full bg-secondary text-foreground hover:bg-secondary" data-testid="prep-exec-status">{sheet.status === "sent" ? "Not started" : "In progress"}</Badge>
            </CardContent>
          </Card>
          {isRead ? <p className="flex items-center gap-1.5 rounded-2xl border border-border bg-secondary/40 p-3 text-sm" data-testid="prep-exec-blind-hint"><EyeOff className="h-4 w-4 shrink-0" /> Count what is physically there{scope === "both" ? " — once on the floor and once in the back of house" : scope === "boh" ? " in the back of house" : " on the floor"}. Enter the number you count; nothing else.</p> : null}
          {sheet.notes ? <p className="rounded-2xl border border-border bg-secondary/40 p-3 text-sm" data-testid="prep-exec-notes">Manager note: {sheet.notes}</p> : null}

          {/* Lines */}
          <div className="grid gap-3 md:grid-cols-2" data-testid="prep-exec-lines">
            {lines.map((l) => {
              const d = draftFor(l);
              const isDone = ["prepared", "unable", "counted"].includes(l.status) && !d.editing;
              const step = stepFor(l.unit);
              const qtyNum = Number(d.qty) || 0;
              const variance = !isRead && l.kind === "produce" && l.actual_qty != null ? Number(l.actual_qty) - Number(l.target_qty) : null;
              const queued = queuedIds.has(l.id) || !!pendingLocal[l.id];
              if (isRead) {
                const countBox = (key, label, testKey) => {
                  const v = d[key];
                  const n = Number(v) || 0;
                  return (
                    <div className="rounded-2xl border border-border p-3" data-testid={`prep-exec-${testKey}-${l.id}`}>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
                      {isDone ? (
                        <p className="font-['Space_Grotesk'] text-3xl font-semibold tabular-nums">{fmt(key === "floor" ? l.counted_floor : l.counted_boh)} <span className="text-base font-medium text-muted-foreground">{l.unit}</span></p>
                      ) : (
                        <div className="mt-1 flex items-center gap-1">
                          <Button type="button" variant="outline" size="icon" className="h-12 w-12 shrink-0 rounded-xl" aria-label={`Less ${label}`} onClick={() => setDraft(l, { [key]: String(Math.max(0, +(n - step).toFixed(3))) })} data-testid={`prep-exec-${testKey}-minus-${l.id}`}><Minus className="h-5 w-5" /></Button>
                          <Input type="number" inputMode="decimal" min="0" step="0.01" value={v} placeholder="0" onChange={(e) => setDraft(l, { [key]: e.target.value })} className="h-12 min-w-0 rounded-xl text-center text-2xl font-semibold tabular-nums" data-testid={`prep-exec-${testKey}-qty-${l.id}`} />
                          <Button type="button" variant="outline" size="icon" className="h-12 w-12 shrink-0 rounded-xl" aria-label={`More ${label}`} onClick={() => setDraft(l, { [key]: String(+(n + step).toFixed(3)) })} data-testid={`prep-exec-${testKey}-plus-${l.id}`}><Plus className="h-5 w-5" /></Button>
                        </div>
                      )}
                    </div>
                  );
                };
                return (
                  <Card key={l.id} className={`rounded-[24px] border ${isDone ? "border-emerald-500/50 bg-emerald-500/5" : "border-border bg-card"}`} data-testid={`prep-exec-line-${l.id}`}>
                    <CardContent className="space-y-3 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-lg font-semibold leading-tight" data-testid={`prep-exec-line-name-${l.id}`}>{l.name}</p>
                          <p className="text-xs text-muted-foreground">Count in {l.unit || "pcs"}{l.group ? ` · ${l.group}` : ""}</p>
                        </div>
                        {queued ? <Badge variant="outline" className="shrink-0 gap-1 rounded-full border-amber-500/50 text-amber-700 dark:text-amber-300" data-testid={`prep-exec-line-queued-${l.id}`}><CloudOff className="h-3 w-3" /> syncing</Badge>
                          : isDone ? <Badge className="shrink-0 rounded-full bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-300" data-testid={`prep-exec-line-status-${l.id}`}>Counted</Badge> : null}
                      </div>
                      <div className={`grid gap-3 ${wantFloor && wantBoh ? "grid-cols-2" : "grid-cols-1"}`}>
                        {wantFloor ? countBox("floor", "Floor count", "floor") : null}
                        {wantBoh ? countBox("boh", "Back of house count", "boh") : null}
                      </div>
                      {l.staff_note && isDone ? <p className="text-xs text-muted-foreground" data-testid={`prep-exec-line-note-${l.id}`}>Note: {l.staff_note}</p> : null}
                      <div className="flex flex-wrap gap-2">
                        {isDone ? (
                          <Button variant="outline" className="h-12 flex-1 rounded-xl" disabled={busy === l.id} onClick={() => setDraft(l, { editing: true })} data-testid={`prep-exec-edit-${l.id}`}><Pencil className="h-4 w-4" /> Change</Button>
                        ) : (
                          <Button className="h-14 flex-1 rounded-xl bg-[hsl(var(--primary))] text-base font-semibold text-primary-foreground hover:bg-[hsl(var(--primary))]/90" disabled={busy === l.id} onClick={() => recordCount(l)} data-testid={`prep-exec-count-${l.id}`}>
                            {busy === l.id ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />} Save count
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              }
              return (
                <Card key={l.id} className={`rounded-[24px] border ${l.status === "prepared" && isDone ? "border-emerald-500/50 bg-emerald-500/5" : l.status === "unable" && isDone ? "border-destructive/40 bg-destructive/5" : "border-border bg-card"}`} data-testid={`prep-exec-line-${l.id}`}>
                  <CardContent className="space-y-3 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-lg font-semibold leading-tight" data-testid={`prep-exec-line-name-${l.id}`}>{l.name}</p>
                        <p className="text-xs text-muted-foreground">{l.kind === "task" ? (Number(l.target_qty) > 0 ? <>Checklist task · prep <b className="text-foreground">{fmt(l.target_qty)} {l.unit || ""}</b></> : "Checklist task") : <>{l.source_location_name || "BOH"} → {l.output_location_name || "Floor"}</>}{l.note ? ` · ${l.note}` : ""}</p>
                      </div>
                      {queued ? <Badge variant="outline" className="shrink-0 gap-1 rounded-full border-amber-500/50 text-amber-700 dark:text-amber-300" data-testid={`prep-exec-line-queued-${l.id}`}><CloudOff className="h-3 w-3" /> syncing</Badge>
                        : isDone ? <Badge className={`shrink-0 rounded-full ${l.status === "prepared" ? "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-300" : "bg-destructive/10 text-destructive hover:bg-destructive/10"}`} data-testid={`prep-exec-line-status-${l.id}`}>{l.status === "prepared" ? "Prepared" : "Unable"}</Badge> : null}
                    </div>

                    {l.kind === "produce" ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-secondary/60 p-3" data-testid={`prep-exec-target-${l.id}`}>
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Target (manager)</p>
                          <p className="font-['Space_Grotesk'] text-3xl font-semibold tabular-nums">{fmt(l.target_qty)} <span className="text-base font-medium text-muted-foreground">{l.unit}</span></p>
                        </div>
                        <div className="rounded-2xl border border-border p-3" data-testid={`prep-exec-actual-${l.id}`}>
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Actual prepared</p>
                          {isDone ? (
                            <p className="font-['Space_Grotesk'] text-3xl font-semibold tabular-nums">{fmt(l.actual_qty)} <span className="text-base font-medium text-muted-foreground">{l.unit}</span>
                              {variance ? <span className={`ml-1 text-sm font-semibold ${variance < 0 ? "text-destructive" : "text-emerald-600"}`}>({variance > 0 ? "+" : ""}{fmt(variance)})</span> : null}</p>
                          ) : (
                            <div className="mt-1 flex items-center gap-1">
                              <Button type="button" variant="outline" size="icon" className="h-12 w-12 shrink-0 rounded-xl" aria-label="Less" onClick={() => setDraft(l, { qty: String(Math.max(0, +(qtyNum - step).toFixed(3))) })} data-testid={`prep-exec-minus-${l.id}`}><Minus className="h-5 w-5" /></Button>
                              <Input type="number" inputMode="decimal" min="0" step="0.01" value={d.qty} onChange={(e) => setDraft(l, { qty: e.target.value })} className="h-12 min-w-0 rounded-xl text-center text-2xl font-semibold tabular-nums" data-testid={`prep-exec-qty-${l.id}`} />
                              <Button type="button" variant="outline" size="icon" className="h-12 w-12 shrink-0 rounded-xl" aria-label="More" onClick={() => setDraft(l, { qty: String(+(qtyNum + step).toFixed(3)) })} data-testid={`prep-exec-plus-${l.id}`}><Plus className="h-5 w-5" /></Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}

                    {l.staff_note && isDone ? <p className="text-xs text-muted-foreground" data-testid={`prep-exec-line-note-${l.id}`}>Note: {l.staff_note}</p> : null}

                    <div className="flex flex-wrap gap-2">
                      {isDone ? (
                        <Button variant="outline" className="h-12 flex-1 rounded-xl" disabled={busy === l.id} onClick={() => setDraft(l, { editing: true, qty: l.actual_qty != null ? String(l.actual_qty) : String(l.target_qty ?? "") })} data-testid={`prep-exec-edit-${l.id}`}><Pencil className="h-4 w-4" /> Change</Button>
                      ) : (
                        <>
                          <Button className="h-14 flex-1 rounded-xl bg-[hsl(var(--primary))] text-base font-semibold text-primary-foreground hover:bg-[hsl(var(--primary))]/90" disabled={busy === l.id} onClick={() => recordLine(l, "prepared")} data-testid={`prep-exec-mark-${l.id}`}>
                            {busy === l.id ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />} {l.kind === "task" ? "Done" : "Mark prepared"}
                          </Button>
                          <Button variant="outline" className="h-14 rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" disabled={busy === l.id} onClick={() => setUnableDlg({ line: l })} data-testid={`prep-exec-unable-${l.id}`}><XCircle className="h-5 w-5" /> <span className="hidden sm:inline">Unable</span></Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {!lines.length ? <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="prep-exec-no-lines">This sheet has no lines yet.</p> : null}
        </>
      )}

      {/* Unable to complete */}
      <Dialog open={!!unableDlg} onOpenChange={(o) => { if (!o) setUnableDlg(null); }}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="prep-exec-unable-dialog">
          <DialogHeader><DialogTitle className="font-['Space_Grotesk'] text-2xl">Unable to complete</DialogTitle><DialogDescription>{unableDlg?.line?.name} — tell the manager why, and how much (if any) you did manage to prepare.</DialogDescription></DialogHeader>
          {unableDlg ? (
            <div className="space-y-3">
              {unableDlg.line.kind === "produce" ? (
                <div><Label className="text-xs">Quantity prepared ({unableDlg.line.unit})</Label>
                  <Input type="number" inputMode="decimal" min="0" step="0.01" value={unableDlg.qty ?? "0"} onChange={(e) => setUnableDlg((u) => ({ ...u, qty: e.target.value }))} className="mt-1 h-12 rounded-xl text-lg tabular-nums" data-testid="prep-exec-unable-qty" /></div>
              ) : null}
              <div><Label className="text-xs">Reason</Label>
                <div className="mt-1 flex flex-wrap gap-2">{["Out of ingredients", "No time", "Equipment issue", "Other"].map((r) => <button key={r} type="button" onClick={() => setUnableDlg((u) => ({ ...u, note: r }))} className={`h-10 rounded-xl border px-3 text-sm ${unableDlg.note === r ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10" : "border-border bg-card"}`} data-testid={`prep-exec-unable-reason-${r.replace(/\s+/g, "-").toLowerCase()}`}>{r}</button>)}</div>
                <Textarea value={unableDlg.note || ""} onChange={(e) => setUnableDlg((u) => ({ ...u, note: e.target.value }))} className="mt-2 rounded-xl" rows={2} placeholder="Details (optional)" data-testid="prep-exec-unable-note" /></div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" className="h-12 rounded-xl" onClick={() => setUnableDlg(null)} data-testid="prep-exec-unable-cancel">Back</Button>
            <Button className="h-12 rounded-xl" variant="destructive" disabled={!!busy} onClick={async () => { const u = unableDlg; setUnableDlg(null); await recordLine(u.line, "unable", { actual_qty: Number(u.qty) || 0, note: u.note || "" }); }} data-testid="prep-exec-unable-confirm"><XCircle className="h-4 w-4" /> Mark unable</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit */}
      <Dialog open={submitDlg} onOpenChange={setSubmitDlg}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="prep-exec-submit-dialog">
          <DialogHeader><DialogTitle className="font-['Space_Grotesk'] text-2xl">{isRead ? "Submit counts?" : "Submit to manager?"}</DialogTitle>
            <DialogDescription>{isRead
              ? <>{done} of {lines.length} items counted.{lines.length - done > 0 ? ` ${lines.length - done} item${lines.length - done === 1 ? "" : "s"} will be sent as “not counted”.` : ""} The manager compares your counts with the expected stock — nothing changes in stock.</>
              : <>{done} of {lines.length} lines are done.{lines.length - done > 0 ? ` The ${lines.length - done} unfinished line${lines.length - done === 1 ? "" : "s"} will be marked “Unable” with 0 prepared.` : ""} The manager reviews your actual quantities and confirms production — stock only moves then.</>}</DialogDescription></DialogHeader>
          <div className="space-y-2 text-sm" data-testid="prep-exec-submit-summary">
            {isRead
              ? lines.map((l) => <div key={l.id} className="flex items-center justify-between rounded-xl bg-secondary/50 px-3 py-2"><span className="truncate">{l.name}</span><span className="tabular-nums">{l.status === "counted" ? <>{wantFloor ? <>floor <b>{fmt(l.counted_floor)}</b></> : null}{wantFloor && wantBoh ? " · " : ""}{wantBoh ? <>BOH <b>{fmt(l.counted_boh)}</b></> : null} {l.unit}</> : <span className="text-muted-foreground">not counted</span>}</span></div>)
              : lines.filter((l) => l.kind === "produce").map((l) => <div key={l.id} className="flex items-center justify-between rounded-xl bg-secondary/50 px-3 py-2"><span className="truncate">{l.name}</span><span className="tabular-nums">target {fmt(l.target_qty)} · actual <b>{l.actual_qty != null ? fmt(l.actual_qty) : (l.status === "pending" ? "—" : "0")}</b> {l.unit}</span></div>)}
          </div>
          <div><Label className="text-xs">Note for the manager (optional)</Label><Textarea value={submitNote} onChange={(e) => setSubmitNote(e.target.value)} className="mt-1 rounded-xl" rows={2} data-testid="prep-exec-submit-note" /></div>
          <DialogFooter>
            <Button variant="outline" className="h-12 rounded-xl" onClick={() => setSubmitDlg(false)} data-testid="prep-exec-submit-cancel">Keep working</Button>
            <Button className="h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" disabled={busy === "submit"} onClick={submit} data-testid="prep-exec-submit-confirm">{busy === "submit" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** Small header button for the Kitchen / Bar dashboards: opens the prep view and shows how many sheets are waiting. */
export const PrepModeButton = ({ http, headers, station, active, onClick }) => {
  const [count, setCount] = useState(0);
  const [readCount, setReadCount] = useState(0);
  const [pulse, setPulse] = useState(false);
  const load = useCallback(async () => {
    if (!headers) return;
    try {
      const r = await cachedGet(http, "/station/prep-sheets", { headers, params: { station } });
      const act = r.data?.active || [];
      setCount(act.length);
      setReadCount(act.filter((s) => s.sheet_type === "stock_read").length);
    } catch (_) { /* keep last */ }
  }, [http, headers, station]);
  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, [load]);
  useEffect(() => {
    const unsub = realtime.subscribe((msg) => {
      const t = String(msg?.type || "");
      if (!/^prep_sheet_|^production_confirmed/.test(t)) return;
      if (msg?.payload?.station && msg.payload.station !== station) return;
      load();
      // Audit-log echoes carry no sheet_id — only the domain event should chime.
      if (t === "prep_sheet_sent" && msg?.payload?.sheet_id) { setPulse(true); setTimeout(() => setPulse(false), 8000); toast.info(msg?.payload?.sheet_type === "stock_read" ? `Stock read to count: ${msg?.payload?.title || "today"}` : `New ${station === "bar" ? "bar" : "kitchen"} prep sheet: ${msg?.payload?.title || "today"}`, { id: "prep-sent" }); }
    });
    return unsub;
  }, [load, station]);
  // Session 64 — the button covers prep sheets AND stock reads. When only a
  // stock read is waiting, say so; otherwise "Prep / Stock".
  const label = readCount > 0 && readCount === count ? "Stock read" : "Prep / Stock";
  const title = readCount > 0 ? `${readCount} stock read${readCount === 1 ? "" : "s"} to count${count > readCount ? ` · ${count - readCount} prep sheet${count - readCount === 1 ? "" : "s"}` : ""}` : "Today's prep sheets and stock reads";
  return (
    <Button variant={active ? "default" : "outline"} className={`relative h-10 rounded-xl ${pulse ? "ring-2 ring-[hsl(var(--accent))]" : ""}`} onClick={onClick} data-testid={`${station}-prep-mode-button`} title={title}>
      {readCount > 0 ? <ClipboardCheck className="h-4 w-4" /> : <ClipboardList className="h-4 w-4" />}
      <span data-testid={`${station}-prep-mode-label`}>{label}</span>
      {count > 0 ? <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[hsl(var(--accent))] px-1.5 text-[11px] font-bold text-[hsl(var(--accent-foreground))]" data-testid={`${station}-prep-mode-count`}>{count}</span> : null}
    </Button>
  );
};
